import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '@/features/auth/auth-context';
import { ApiError, type ApiErrorKey } from '@/services/api/errors';
import { OrganizationApi } from '@/services/api/organization-api';
import type { OrganizationSettings, OrganizationUpdateRequest } from '@/services/api/types';

export interface UseOrganizationSettingsResult {
  data: OrganizationSettings | null;
  loading: boolean;
  /** Translatable error key when the initial fetch failed, or null. */
  loadError: ApiErrorKey | null;
  saving: boolean;
  /** Translatable error key when the last save attempt failed, or null. */
  saveError: ApiErrorKey | null;
  saved: boolean;
  save: (update: OrganizationUpdateRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

export function useOrganizationSettings(organization?: OrganizationApi): UseOrganizationSettingsResult {
  const auth = useContext(AuthContext);

  const injectedApi = useRef(organization);
  useEffect(() => {
    injectedApi.current = organization;
  }, [organization]);
  const realApiRef = useRef<OrganizationApi | null>(null);

  const getApi = useCallback((): OrganizationApi => {
    if (injectedApi.current) return injectedApi.current;
    const client = auth?.client;
    if (!client) throw new ApiError('unknown', 'generic');
    if (!realApiRef.current) realApiRef.current = new OrganizationApi(client);
    return realApiRef.current;
  }, [auth]);

  const [data, setData] = useState<OrganizationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiErrorKey | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiErrorKey | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await getApi().get(controller.signal);
        if (controller.signal.aborted) return;
        setData(result);
        setLoadError(null);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'aborted') return;
        setLoadError(err instanceof ApiError ? err.key : 'generic');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [getApi]);

  const save = useCallback(
    async (update: OrganizationUpdateRequest): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      try {
        const result = await getApi().update(update);
        setData(result);
        setSaved(true);
        return true;
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.key : 'generic');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getApi],
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);

  return { data, loading, loadError, saving, saveError, saved, save, clearSaveError };
}
